'use client';

import useIsMobile from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import clsx from 'clsx';
import Image from 'next/image';
import * as React from 'react';

const Dialog = DialogPrimitive.Root;
const DialogPortal = DialogPrimitive.Portal;
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className='fixed inset-0 z-[200] bg-#403732/50 data-[state=open]_animate-in  data-[state=closed]_animate-out data-[state=closed]_fade-out-0 data-[state=open]_fade-in-0 lg_bg-#403732/60'
    {...props}
  />
));

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'duration-200 fixed left-1/2 top-1/2 z-[200] -translate-x-1/2 -translate-y-1/2 data-[state=open]_animate-in data-[state=closed]_animate-out data-[state=closed]_fade-out-0 data-[state=open]_fade-in-0 data-[state=closed]_zoom-out-95 data-[state=open]_zoom-in-95 data-[state=closed]_slide-out-to-left-1/2 data-[state=closed]_slide-out-to-top-[48%] data-[state=open]_slide-in-from-left-1/2  data-[state=open]_slide-in-from-top-[48%] ',
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className='text-lg font-semibold leading-none tracking-tight'
    {...props}
  />
));

DialogTitle.displayName = DialogPrimitive.Title.displayName;

interface Props {
  open: boolean;
  onOpenChange: (nextOpen: boolean) => void;
  previewImages: {
    items: {
      src: string;
      mobileSrc: string;
      alt: string;
      width: number;
      height: number;
      mobileWidth: number;
      mobileHeight: number;
    }[];
    curIndex: number;
  };
  setPreviewImages: React.Dispatch<
    React.SetStateAction<{
      items: {
        src: string;
        mobileSrc: string;
        alt: string;
        width: number;
        height: number;
        mobileWidth: number;
        mobileHeight: number;
      }[];
      curIndex: number;
    }>
  >;
}

const MediaDialog = (props: Props) => {
  const {
    open,
    onOpenChange,
    previewImages: { items, curIndex },
    setPreviewImages,
  } = props;
  const isMobile = useIsMobile();
  const currentImage = curIndex < items.length ? items[curIndex] : null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className={clsx('flex w-full max-w-[370px] lg_max-w-[1212px] lg_gap-x-0 ', 'outline-none')}
        aria-describedby={undefined}
      >
        <div
          className='flex w-[25px] flex-none items-center justify-center pr-[5px] lg_w-[56px] lg_pr-0'
          onClick={() => {
            onOpenChange(false);
          }}
        >
          <button
            type='button'
            className={clsx(
              'flex h-11 w-full items-center justify-center lg_h-[53px]',
              items.length <= 1 && 'invisible',
            )}
            onClick={e => {
              e.stopPropagation();
              setPreviewImages(x => {
                let nextCurIndex = x.curIndex - 1;

                if (nextCurIndex < 0) {
                  nextCurIndex = items.length - 1;
                }

                nextCurIndex = Math.max(0, nextCurIndex);
                nextCurIndex = Math.min(items.length - 1, nextCurIndex);

                return {
                  ...x,
                  curIndex: nextCurIndex,
                };
              });
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='19'
              height='36'
              fill='none'
              viewBox='0 0 19 36'
            >
              <path
                stroke='#fff'
                strokeWidth='2'
                d='M18 1.5 2 18l16 16.5'
              />
            </svg>
          </button>
        </div>
        <div className='relative flex-1 bg-white py-8 lg_py-[55px]'>
          <button
            type='button'
            className='absolute right-3 top-3 lg_right-[22px] lg_top-[22px]'
            onClick={() => {
              onOpenChange(false);
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              fill='none'
              viewBox='0 0 18 18'
              className='h-auto w-3 lg_w-4'
            >
              <path
                stroke='#80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.5'
                d='M17 17 1 1m16 0L1 17'
              />
            </svg>
          </button>

          {currentImage ? (
            <div className='flex min-h-[250px] items-center justify-center lg_min-h-[496px]'>
              <Image
                alt={currentImage.alt}
                className='block'
                src={isMobile ? currentImage.mobileSrc : currentImage.src}
                width={isMobile ? currentImage.mobileWidth : currentImage.width}
                height={isMobile ? currentImage.mobileHeight : currentImage.height}
                quality={100}
              />
            </div>
          ) : null}

          <button
            type='button'
            className='aw-primary-btn mx-auto flex h-9 w-full max-w-[157px] items-center justify-center rounded-[3px] text-[15px] leading-[21px] lg_mt-10'
            onClick={() => {
              onOpenChange(false);
            }}
          >
            OKAY
          </button>
        </div>
        <div
          className={clsx('flex w-[25px] flex-none  items-center justify-center pl-[5px] lg_w-[56px] lg_pl-0')}
          onClick={() => {
            onOpenChange(false);
          }}
        >
          <button
            type='button'
            className={clsx(
              'flex h-11 w-full items-center justify-center lg_h-[53px] ',
              items.length <= 1 && 'invisible',
            )}
            onClick={e => {
              e.stopPropagation();
              setPreviewImages(x => {
                let nextCurIndex = x.curIndex + 1;

                if (nextCurIndex > items.length - 1) {
                  nextCurIndex = 0;
                }

                nextCurIndex = Math.max(0, nextCurIndex);
                nextCurIndex = Math.min(items.length - 1, nextCurIndex);

                return {
                  ...x,
                  curIndex: nextCurIndex,
                };
              });
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='19'
              height='36'
              fill='none'
              viewBox='0 0 19 36'
            >
              <path
                stroke='#fff'
                strokeWidth='2'
                d='M1 1.5 17 18 1 34.5'
              />
            </svg>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaDialog;
